import { useEffect, useState } from "react";
import {
  adminListAudios, adminCreateAudio, adminUpdateAudio, adminDeleteAudio,
  adminListAudioSections, adminCreateAudioSection, adminUpdateAudioSection, adminDeleteAudioSection,
  adminListLiveChannels, adminCreateLiveChannel, adminUpdateLiveChannel, adminDeleteLiveChannel,
} from "@/lib/api";
import { AUDIO_CATEGORIES } from "@/lib/audioCategories";
import AudioUploader from "@/components/AudioUploader";
import { Music, Plus, Trash2, Pencil, ExternalLink, Loader2, X, Layers, Radio, ChevronUp, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const TYPES = ["mantra", "stotram", "katha", "namasmarna", "aarati", "bhajan", "vedam", "pravachan"];
const EMPTY = { title: "", title_hi: "", kind: "youtube", url: "", category: "", section: "", atype: "", deity: "", image: "", loop: false, show_on_home: false, show_on_navratri: false, show_on_japa: false, order: 0, hidden: false };

/* ─────────── Tracks ─────────── */
function TracksTab({ sections }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => adminListAudios().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY); setEditId(null); };
  const secName = (id) => sections.find((s) => s.id === id)?.title || "—";

  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    if (!form.url.trim()) { toast.error(form.kind === "youtube" ? "Paste a YouTube URL" : "Upload or paste an audio URL"); return; }
    setSaving(true);
    try {
      if (editId) { await adminUpdateAudio(editId, form); toast("Track updated"); }
      else { await adminCreateAudio(form); toast("Track added"); }
      reset(); load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  const edit = (a) => { setForm({ ...EMPTY, ...a }); setEditId(a.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!window.confirm("Delete this track?")) return; try { await adminDeleteAudio(id); load(); } catch { toast.error("Delete failed"); } };
  const toggle = async (a, k) => { try { await adminUpdateAudio(a.id, { [k]: !a[k] }); load(); } catch { toast.error("Update failed"); } };

  return (
    <div data-testid="admin-audios-tracks">
      <div className="mb-8 border border-line bg-surface p-5" data-testid="audio-composer">
        <div className="mb-3 flex items-center justify-between">
          <p className="overline-label text-gold">{editId ? "Edit track" : "New track"}</p>
          {editId && <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"><X className="h-3.5 w-3.5" /> Cancel edit</button>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="audio-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Title (English)" className={inp} />
          <input data-testid="audio-title-hi" value={form.title_hi} onChange={(e) => set("title_hi", e.target.value)} placeholder="शीर्षक (हिन्दी)" className={inp} />
          <select data-testid="audio-section" value={form.section} onChange={(e) => set("section", e.target.value)} className={inp}>
            <option value="">— Section —</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <select data-testid="audio-atype" value={form.atype} onChange={(e) => set("atype", e.target.value)} className={inp}>
            <option value="">— Type —</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="audio-kind" value={form.kind} onChange={(e) => set("kind", e.target.value)} className={inp}>
            <option value="youtube">YouTube link</option>
            <option value="audio">Uploaded / audio file</option>
          </select>
          <input data-testid="audio-deity" value={form.deity} onChange={(e) => set("deity", e.target.value)} placeholder="Deity (optional)" className={inp} />
          <input data-testid="audio-category" list="audio-cats" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Category / tag" className={inp} />
          <datalist id="audio-cats">{AUDIO_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
          <input data-testid="audio-order" type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} placeholder="Order" className={inp} />
          <input data-testid="audio-image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="Cover image URL (optional)" className={`${inp} sm:col-span-2`} />
        </div>
        <div className="mt-3">
          {form.kind === "youtube"
            ? <input data-testid="audio-url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="YouTube URL (https://youtu.be/...)" className={inp} />
            : <AudioUploader label="Audio file" testid="audio-file" value={form.url} onChange={(v) => set("url", v)} />}
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-2">
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="audio-loop" checked={form.loop} onChange={(e) => set("loop", e.target.checked)} /> Loop</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="audio-home" checked={form.show_on_home} onChange={(e) => set("show_on_home", e.target.checked)} /> Feature on Homepage</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="audio-navratri" checked={form.show_on_navratri} onChange={(e) => set("show_on_navratri", e.target.checked)} /> Feature on Navratri</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="audio-japa" checked={form.show_on_japa} onChange={(e) => set("show_on_japa", e.target.checked)} /> Feature on Japa page</label>
        </div>
        <button data-testid="audio-save" onClick={save} disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Save changes" : "Add track"}
        </button>
      </div>

      {items === null ? <p className="py-16 text-center text-ink-3">…</p> : items.length === 0 ? (
        <p className="py-16 text-center text-ink-3">No tracks yet.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => (
            <li key={a.id} data-testid={`audio-item-${a.id}`} className={`border border-line bg-surface p-4 ${a.hidden ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-serif-latin text-base text-ink">{a.title}{a.title_hi ? ` · ${a.title_hi}` : ""}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-3">
                    <span className="rounded-full bg-saffron/15 px-2 py-0.5 text-saffron">{secName(a.section)}</span>
                    {a.atype && <span className="rounded-full bg-gold/15 px-2 py-0.5 text-gold">{a.atype}</span>}
                    <span>{a.kind === "youtube" ? "YouTube" : "File"}</span>
                    {a.deity && <span>· {a.deity}</span>}
                    {a.loop && <span>· loop</span>}
                    {!a.url && <span className="text-amber-600">· no URL yet</span>}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => edit(a)} title="Edit" className="p-2 text-ink-3 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(a.id)} title="Delete" className="p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-3 border-t border-line pt-2 text-xs text-ink-2">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!a.show_on_home} onChange={() => toggle(a, "show_on_home")} /> Homepage</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!a.show_on_navratri} onChange={() => toggle(a, "show_on_navratri")} /> Navratri</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!a.show_on_japa} onChange={() => toggle(a, "show_on_japa")} /> Japa</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!a.hidden} onChange={() => toggle(a, "hidden")} /> Hidden</label>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────── Sections ─────────── */
const SEC_EMPTY = { title: "", title_hi: "", deity: "", order: 0, hidden: false };
function SectionsTab({ reloadSections }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(SEC_EMPTY);
  const [editId, setEditId] = useState(null);
  const load = () => adminListAudioSections().then((d) => { setItems(d); reloadSections(); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(SEC_EMPTY); setEditId(null); };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a section title"); return; }
    try { editId ? await adminUpdateAudioSection(editId, form) : await adminCreateAudioSection(form); toast("Saved"); reset(); load(); }
    catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this section? Tracks stay but become unassigned.")) return; try { await adminDeleteAudioSection(id); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div data-testid="admin-audio-sections">
      <div className="mb-8 border border-line bg-surface p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="overline-label text-gold">{editId ? "Edit section" : "New section"}</p>
          {editId && <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"><X className="h-3.5 w-3.5" /> Cancel</button>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="section-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Section title (e.g. Daily Pooja)" className={inp} />
          <input data-testid="section-title-hi" value={form.title_hi} onChange={(e) => set("title_hi", e.target.value)} placeholder="शीर्षक (हिन्दी)" className={inp} />
          <input data-testid="section-deity" value={form.deity} onChange={(e) => set("deity", e.target.value)} placeholder="Linked deity (optional)" className={inp} />
          <input data-testid="section-order" type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} placeholder="Order" className={inp} />
        </div>
        <button data-testid="section-save" onClick={save} className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]">
          <Plus className="h-4 w-4" /> {editId ? "Save section" : "Add section"}
        </button>
      </div>
      {items === null ? <p className="py-10 text-center text-ink-3">…</p> : (
        <ul className="space-y-2">
          {items.map((s) => (
            <li key={s.id} className={`flex items-center justify-between border border-line bg-surface p-4 ${s.hidden ? "opacity-60" : ""}`} data-testid={`section-item-${s.id}`}>
              <div>
                <p className="font-serif-latin text-base text-ink">{s.title}{s.title_hi ? ` · ${s.title_hi}` : ""}</p>
                <p className="text-xs text-ink-3">#{s.order}{s.deity ? ` · ${s.deity}` : ""}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm({ ...SEC_EMPTY, ...s }); setEditId(s.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="p-2 text-ink-3 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(s.id)} className="p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────── Live Channels ─────────── */
const CH_EMPTY = { title: "", title_hi: "", lang: "hi", deity: "", color: "#C75000", order: 0, hidden: false, track_ids: [] };
function ChannelsTab() {
  const [items, setItems] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [form, setForm] = useState(CH_EMPTY);
  const [editId, setEditId] = useState(null);
  const load = () => adminListLiveChannels().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); adminListAudios().then((d) => setTracks((d || []).filter((t) => t.url))).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(CH_EMPTY); setEditId(null); };

  const inChannel = (id) => form.track_ids.includes(id);
  const toggleTrack = (id) => set("track_ids", inChannel(id) ? form.track_ids.filter((x) => x !== id) : [...form.track_ids, id]);
  const move = (i, dir) => { const ni = i + dir; if (ni < 0 || ni >= form.track_ids.length) return; const arr = [...form.track_ids]; [arr[i], arr[ni]] = [arr[ni], arr[i]]; set("track_ids", arr); };
  const trackTitle = (id) => tracks.find((t) => t.id === id)?.title || id;

  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a channel title"); return; }
    try { editId ? await adminUpdateLiveChannel(editId, form) : await adminCreateLiveChannel(form); toast("Saved"); reset(); load(); }
    catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this channel?")) return; try { await adminDeleteLiveChannel(id); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div data-testid="admin-live-channels">
      <div className="mb-8 border border-line bg-surface p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="overline-label text-gold">{editId ? "Edit channel" : "New live channel"}</p>
          {editId && <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"><X className="h-3.5 w-3.5" /> Cancel</button>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="channel-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Channel title (e.g. Hindi Live)" className={inp} />
          <input data-testid="channel-title-hi" value={form.title_hi} onChange={(e) => set("title_hi", e.target.value)} placeholder="शीर्षक (हिन्दी)" className={inp} />
          <input data-testid="channel-lang" value={form.lang} onChange={(e) => set("lang", e.target.value)} placeholder="Language code (hi / ta / en / sa)" className={inp} />
          <div className="flex items-center gap-2">
            <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="h-10 w-12 border border-line bg-parchment" />
            <input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} placeholder="Order" className={inp} />
          </div>
        </div>
        <div className="mt-4">
          <p className="mb-2 text-xs uppercase tracking-wider text-ink-3">Playlist — pick & order the tracks on this channel</p>
          {form.track_ids.length > 0 && (
            <ol className="mb-3 space-y-1.5 border border-line bg-parchment p-2" data-testid="channel-playlist">
              {form.track_ids.map((id, i) => (
                <li key={id} className="flex items-center gap-2 text-sm text-ink">
                  <span className="w-5 text-right text-xs text-ink-3">{i + 1}.</span>
                  <span className="min-w-0 flex-1 truncate">{trackTitle(id)}</span>
                  <button onClick={() => move(i, -1)} className="p-1 text-ink-3 hover:text-saffron"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, 1)} className="p-1 text-ink-3 hover:text-saffron"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => toggleTrack(id)} className="p-1 text-ink-3 hover:text-[var(--saffron-dark)]"><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ol>
          )}
          <div className="max-h-56 overflow-y-auto border border-line">
            {tracks.length === 0 ? <p className="p-3 text-xs text-ink-3">Add tracks first (Tracks tab).</p> : tracks.map((t) => (
              <button key={t.id} onClick={() => toggleTrack(t.id)} data-testid={`channel-track-${t.id}`} className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-sm last:border-0 hover:bg-parchment">
                <span className={`flex h-4 w-4 items-center justify-center border ${inChannel(t.id) ? "border-saffron bg-saffron text-white" : "border-line"}`}>{inChannel(t.id) && <Check className="h-3 w-3" />}</span>
                <span className="min-w-0 flex-1 truncate text-ink">{t.title}</span>
                <span className="shrink-0 text-xs text-ink-3">{t.atype || t.category}</span>
              </button>
            ))}
          </div>
        </div>
        <button data-testid="channel-save" onClick={save} className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]">
          <Plus className="h-4 w-4" /> {editId ? "Save channel" : "Add channel"}
        </button>
      </div>
      {items === null ? <p className="py-10 text-center text-ink-3">…</p> : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li key={c.id} className={`flex items-center justify-between border border-line bg-surface p-4 ${c.hidden ? "opacity-60" : ""}`} data-testid={`channel-item-${c.id}`}>
              <div className="flex items-center gap-3">
                <span className="h-8 w-8 rounded-full" style={{ background: c.color || "#C75000" }} />
                <div>
                  <p className="font-serif-latin text-base text-ink">{c.title} <span className="text-xs uppercase text-ink-3">{c.lang}</span></p>
                  <p className="text-xs text-ink-3">{(c.track_ids || []).length} tracks · #{c.order}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm({ ...CH_EMPTY, ...c }); setEditId(c.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="p-2 text-ink-3 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(c.id)} className="p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminAudios() {
  const [tab, setTab] = useState("tracks");
  const [sections, setSections] = useState([]);
  const reloadSections = () => adminListAudioSections().then(setSections).catch(() => {});
  useEffect(() => { reloadSections(); }, []);

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`audio-tab-${id}`} className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${tab === id ? "border-saffron text-saffron" : "border-transparent text-ink-2 hover:text-ink"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-audios">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2.5"><Music className="h-6 w-6 text-saffron" /><h1 className="font-serif-latin text-3xl text-ink">Audio Library</h1></div>
        <a href="/audios" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-saffron hover:underline">View page <ExternalLink className="h-3.5 w-3.5" /></a>
      </div>
      <p className="mb-5 text-sm text-ink-3">Build the audio platform: create <b>sections</b> (Daily Pooja, Bhajans, Aarati…), add <b>tracks</b> (YouTube or uploaded) with a type &amp; deity, and curate <b>live channels</b> (Hindi/Tamil/English) that play continuously.</p>

      <div className="mb-6 flex gap-2 border-b border-line">
        <TabBtn id="tracks" icon={Music} label="Tracks" />
        <TabBtn id="sections" icon={Layers} label="Sections" />
        <TabBtn id="channels" icon={Radio} label="Live Channels" />
      </div>

      {tab === "tracks" && <TracksTab sections={sections} />}
      {tab === "sections" && <SectionsTab reloadSections={reloadSections} />}
      {tab === "channels" && <ChannelsTab />}
    </div>
  );
}
